/**
 * Lapis — Compliance & Brand Guard console (Round 41 · Workforce v2, Phase 2).
 * What the gate blocked this week, a "Test a string" box, the admin word
 * lists, the watched policy pages (with diffs), and the audits.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../AdminApp';
import { toast } from 'sonner';
import { ShieldCheck, RefreshCw, Search, Plus, X, FileDiff, ClipboardCheck, AlertTriangle, ExternalLink } from 'lucide-react';

const ago = (iso) => {
  if (!iso) return 'never';
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (isNaN(d)) return '';
  if (d < 3600) return `${Math.max(1, Math.round(d / 60))}m ago`;
  if (d < 86400) return `${Math.round(d / 3600)}h ago`;
  return `${Math.round(d / 86400)}d ago`;
};

const KINDS = [['push', 'Push / campaign'], ['email', 'Email'], ['caption', 'Table Talk caption'], ['ticket_reply', 'Support reply'], ['social', 'Social post'], ['legal', 'Legal text']];

const TermList = ({ title, hint, terms, onChange, color }) => {
  const [draft, setDraft] = useState('');
  const add = () => {
    const t = draft.trim();
    if (!t) return;
    if (terms.some((x) => x.toLowerCase() === t.toLowerCase())) return toast.info('Already on the list');
    onChange([...terms, t]); setDraft('');
  };
  return (
    <div className="bg-white border rounded-xl p-4">
      <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
      <p className="text-xs text-gray-500 mb-2">{hint}</p>
      <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]">
        {terms.length === 0 && <span className="text-xs text-gray-400">None yet.</span>}
        {terms.map((t) => (
          <span key={t} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${color}`}>
            {t}<button onClick={() => onChange(terms.filter((x) => x !== t))} className="hover:text-black" title="Remove"><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} placeholder="word or phrase" className="flex-1 border rounded-lg px-3 py-1.5 text-sm" />
        <button onClick={add} className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50 flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add</button>
      </div>
    </div>
  );
};

const LapisPage = () => {
  const { api } = useAdmin();
  const [ov, setOv] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState('');
  const [test, setTest] = useState({ text: '', kind: 'push' });
  const [verdict, setVerdict] = useState(null);
  const [diff, setDiff] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/api/admin/workforce/lapis/overview');
      setOv(r.data); setCfg(r.data.config); setDirty(false);
    } catch (e) { toast.error('Could not load Lapis'); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy('save');
    try {
      const r = await api.post('/api/admin/workforce/lapis/config', { blocked_terms: cfg.blocked_terms, warn_terms: cfg.warn_terms, allow_terms: cfg.allow_terms, enabled: cfg.enabled });
      setCfg(r.data.config); setDirty(false); toast.success('Saved — every outbound path uses these now');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setBusy(''); }
  };
  const runTest = async () => {
    if (!test.text.trim()) return;
    setBusy('test');
    try { const r = await api.post('/api/admin/workforce/lapis/check', { text: test.text, kind: test.kind }); setVerdict(r.data); }
    catch (e) { toast.error('Check failed'); }
    finally { setBusy(''); }
  };
  const checkPolicies = async () => {
    setBusy('policies');
    try {
      const r = await api.post('/api/admin/workforce/lapis/policies/check');
      toast.success(`Checked ${r.data.checked} · changed ${r.data.changed.length} · failed ${r.data.failed.length}`); load();
    } catch (e) { toast.error('Policy check failed'); }
    finally { setBusy(''); }
  };
  const runAudits = async () => {
    setBusy('audits');
    try { await api.post('/api/admin/workforce/lapis/audits/run'); toast.success('Audits ran — results below'); load(); }
    catch (e) { toast.error('Audit failed'); }
    finally { setBusy(''); }
  };
  const openDiff = async (source) => {
    try { const r = await api.get(`/api/admin/workforce/lapis/policies/${source}/diff`); setDiff(r.data); }
    catch (e) { toast.error('No diff stored for that source'); }
  };
  const setList = (key) => (list) => { setCfg((c) => ({ ...c, [key]: list })); setDirty(true); };

  if (!ov || !cfg) return <div className="p-6 text-sm text-gray-400">Loading Lapis…</div>;

  return (
    <div className="p-6 space-y-5" data-testid="lapis-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-blue-700" /> Lapis — Compliance & Brand Guard</h1>
          <p className="text-sm text-gray-500">Every campaign push, Table Talk post, support reply and email passes here first. Blocked copy never leaves the server; it lands in Decisions → Policy with the reason. The brand is GEMS — “GEMS Live” is allowed but flagged.</p>
        </div>
        <button onClick={load} className="text-xs px-3 py-1.5 border rounded-lg flex items-center gap-1 hover:bg-gray-50"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[{ l: 'Blocked (7 d)', v: ov.blocks_7d, c: ov.blocks_7d ? 'text-red-600' : 'text-gray-700' },
          { l: 'Warned (7 d)', v: ov.warnings_7d, c: 'text-amber-600' },
          { l: 'Policy pages watched', v: ov.policies.length, c: 'text-blue-700' },
          { l: 'Last policy check', v: ov.policies.some((p) => p.last_checked_at) ? ago(ov.policies.map((p) => p.last_checked_at).filter(Boolean).sort().slice(-1)[0]) : 'never', c: 'text-gray-700' }]
          .map((m) => <div key={m.l} className="bg-white border rounded-lg p-3 text-center"><p className={`text-lg font-bold ${m.c}`}>{m.v}</p><p className="text-[10px] text-gray-500">{m.l}</p></div>)}
      </div>

      {/* Test a string */}
      <div className="bg-white border rounded-xl p-4" data-testid="lapis-test">
        <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2"><Search className="w-4 h-4 text-gray-500" /> Test a string</h3>
        <p className="text-xs text-gray-500 mb-2">A dry run — nothing is logged or filed. Paste a push, a caption or an email and see what Lapis would do.</p>
        <div className="flex flex-col md:flex-row gap-2">
          <textarea value={test.text} onChange={(e) => setTest({ ...test, text: e.target.value })} rows={3} className="flex-1 border rounded-lg px-3 py-2 text-sm" placeholder="e.g. Come play Spades tonight — 10,000 members are waiting!" />
          <div className="flex md:flex-col gap-2">
            <select value={test.kind} onChange={(e) => setTest({ ...test, kind: e.target.value })} className="border rounded-lg px-2 py-2 text-sm">
              {KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <button onClick={runTest} disabled={busy === 'test'} className="px-4 py-2 bg-blue-700 text-white rounded-lg text-sm font-medium hover:bg-blue-800 disabled:opacity-50">Check</button>
          </div>
        </div>
        {verdict && (
          <div className={`mt-3 rounded-lg px-3 py-2 text-sm border ${verdict.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'}`} data-testid="lapis-verdict">
            <p className="font-semibold">{verdict.ok ? 'Would send' : `Blocked: ${verdict.blocked_terms.join(', ')}`}</p>
            {verdict.warnings?.length > 0 && <p className="text-amber-700 text-xs mt-1">Warnings (sent, but flagged): {verdict.warnings.map((w) => `${w.rule} — “${w.match}”`).join('; ')}</p>}
            {verdict.ok && !verdict.warnings?.length && <p className="text-xs">Nothing flagged.</p>}
          </div>
        )}
      </div>

      {/* Word lists */}
      <div className="grid md:grid-cols-3 gap-3">
        <TermList title="Extra blocked terms" hint="Added on top of the built-in gambling list and the brand rules. Whole words/phrases, any case." terms={cfg.blocked_terms || []} onChange={setList('blocked_terms')} color="bg-red-100 text-red-800" />
        <TermList title="Watch terms (warn only)" hint="Flagged in the queue item but never stop a send." terms={cfg.warn_terms || []} onChange={setList('warn_terms')} color="bg-amber-100 text-amber-800" />
        <TermList title="Allowed phrases" hint={`Phrases that are fine even though they contain a blocked word. “${ov.legal_entity}” is always allowed.`} terms={cfg.allow_terms || []} onChange={setList('allow_terms')} color="bg-emerald-100 text-emerald-800" />
      </div>
      <div className="flex items-center justify-between bg-white border rounded-xl p-3">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => { setCfg({ ...cfg, enabled: e.target.checked }); setDirty(true); }} className="w-4 h-4" />
          Gate is on {cfg.enabled ? '' : <span className="text-red-600 font-medium">— OFF: outbound copy is not being checked</span>}
        </label>
        <div className="flex items-center gap-2">
          <details className="text-xs text-gray-500"><summary className="cursor-pointer">Built-in lists</summary>
            <div className="mt-1 max-w-md"><p><span className="font-semibold">Always blocked:</span> {ov.rule5_terms.join(', ')}; {ov.brand_terms.join(', ')}.</p>
              <p className="mt-1"><span className="font-semibold">Brand:</span> the brand is “{ov.preferred_brand || 'GEMS'}”. “GEMS Live” is allowed but flagged so copy drifts back to GEMS; “{ov.legal_entity}” is never flagged.</p>
              <p className="mt-1"><span className="font-semibold">Warnings:</span> {ov.warn_rules.join(', ')}, third-party brand names.</p></div>
          </details>
          <button onClick={save} disabled={!dirty || busy === 'save'} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50" data-testid="lapis-save">{busy === 'save' ? 'Saving…' : 'Save'}</button>
        </div>
      </div>

      {/* Recent blocks */}
      <div className="bg-white border rounded-xl">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-500" /> What Lapis stopped or flagged</h3>
          <span className="text-[10px] text-gray-500">Last 50</span>
        </div>
        <div className="divide-y max-h-80 overflow-y-auto">
          {ov.recent.length === 0 && <div className="p-6 text-center text-gray-400 text-sm">Nothing yet — every outbound word has been clean.</div>}
          {ov.recent.map((r) => (
            <div key={r.id} className="p-3 text-sm flex items-start gap-3" data-testid={`lapis-row-${r.id}`}>
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold mt-0.5 shrink-0 ${r.blocked ? (r.fail_open ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800') : 'bg-amber-50 text-amber-700'}`}>{r.blocked ? (r.fail_open ? 'SENT ANYWAY' : 'BLOCKED') : 'WARNED'}</span>
              <div className="min-w-0 flex-1">
                <p className="text-gray-900"><span className="font-medium">{r.agent}</span> · {r.channel}{r.title ? ` · ${r.title}` : ''}</p>
                {r.blocked_terms?.length > 0 && <p className="text-xs text-red-700">Blocked: {r.blocked_terms.join(', ')}</p>}
                {r.warnings?.length > 0 && <p className="text-xs text-amber-700">Flagged: {r.warnings.map((w) => `${w.rule} (“${w.match}”)`).join('; ')}</p>}
                {r.excerpt && <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{r.excerpt}</p>}
              </div>
              <span className="text-[10px] text-gray-400 shrink-0">{ago(r.created_at)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Policy watch */}
      <div className="bg-white border rounded-xl">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><FileDiff className="w-4 h-4 text-blue-600" /> Policy watch</h3>
          <button onClick={checkPolicies} disabled={busy === 'policies'} className="text-xs px-3 py-1.5 border rounded-lg hover:bg-gray-50 disabled:opacity-50">{busy === 'policies' ? 'Checking…' : 'Check now'}</button>
        </div>
        <div className="divide-y">
          {ov.policies.map((p) => (
            <div key={p.source} className="p-3 text-sm flex items-center gap-3 flex-wrap">
              <a href={p.url} target="_blank" rel="noreferrer" className="font-medium text-gray-900 hover:underline flex items-center gap-1">{p.label} <ExternalLink className="w-3 h-3 text-gray-400" /></a>
              <span className="text-xs text-gray-500">checked {ago(p.last_checked_at)}</span>
              {p.last_changed_at && <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">changed {ago(p.last_changed_at)}</span>}
              {p.last_error && <span className="text-xs text-red-600">fetch failed: {p.last_error}</span>}
              {p.has_diff && <button onClick={() => openDiff(p.source)} className="ml-auto text-xs text-blue-700 hover:underline">View diff</button>}
            </div>
          ))}
        </div>
        {diff && (
          <div className="border-t p-3">
            <div className="flex items-center justify-between mb-1"><span className="text-xs font-semibold text-gray-700">{diff.label} — {diff.last_changed_at ? new Date(diff.last_changed_at).toLocaleString() : ''}</span><button onClick={() => setDiff(null)} className="text-xs text-gray-500">Close</button></div>
            <pre className="text-[11px] bg-gray-50 rounded-lg p-3 overflow-x-auto max-h-72 whitespace-pre-wrap">{diff.diff || '(no diff stored)'}</pre>
          </div>
        )}
      </div>

      {/* Audits */}
      <div className="bg-white border rounded-xl">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-emerald-600" /> Audits</h3>
          <button onClick={runAudits} disabled={busy === 'audits'} className="text-xs px-3 py-1.5 border rounded-lg hover:bg-gray-50 disabled:opacity-50">{busy === 'audits' ? 'Running…' : 'Run now'}</button>
        </div>
        <div className="divide-y">
          {ov.audits.length === 0 && <div className="p-6 text-center text-gray-400 text-sm">No audits yet — Lapis runs the age-gate audit weekly and the Terms/Privacy check monthly once it is started.</div>}
          {ov.audits.map((a) => (
            <div key={a.id} className="p-3 text-sm">
              {a.kind === 'age_gate' ? (
                <p className="text-gray-900"><span className="font-medium">Age-gate audit</span> · {a.no_age_on_file} of {a.active_members} active members have no age on file
                  {a.verified_badge_no_age ? <span className="text-red-700"> · {a.verified_badge_no_age} Sapphire-badged</span> : null}
                  {a.agreements_incomplete ? <span className="text-amber-700"> · {a.agreements_incomplete} agreements incomplete</span> : null}</p>
              ) : (
                <p className="text-gray-900"><span className="font-medium">Terms / Privacy check</span> · {a.changed?.length ? <span className="text-amber-700">changed: {a.changed.join(', ')}</span> : 'no change'} · agreements version {a.agreements_version || '—'}</p>
              )}
              <p className="text-[11px] text-gray-400">{ago(a.created_at)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LapisPage;
